/**
 * sn-care-pair.js
 *
 * The Care PDPs (`sections/pawtag-product_care.liquid`, template suffix
 * `care-trial`) put TWO lines in the cart on one click: the tag, and a £0
 * Supernormal Care trial on its Recharge selling plan. The tag is free *because*
 * Care is starting alongside it, so neither line may be bought on its own —
 * without this file a customer could open the cart, delete the trial, and check
 * out with a free tag and no subscription (or delete the tag and be left
 * subscribing to nothing).
 *
 * The pair is marked at add-to-cart time with the hidden `_care_bundle` line item
 * property on the tag; the Care half is recognised by product handle. This file
 * then holds the invariant in two layers:
 *
 *   1. In the cart drawer and on the cart page, the paired lines lose their
 *      quantity controls, and their "Remove" turns into "Remove both" — one
 *      click clears the whole pair rather than half of it.
 *   2. Whatever the cart actually says wins. On load and after every cart
 *      update it re-reads /cart.js and repairs anything that got through
 *      (a stale tab, a hand-typed /cart/change URL, a second window): a half
 *      pair is removed, and a tag quantity edited away from the pet count the
 *      Care rung was priced for is put back.
 *
 * Layer 2 is the one that matters — layer 1 only stops the customer walking into
 * it. Neither can police /checkout itself; a cart validation Shopify Function is
 * the only true backstop, and there is no app for one on this store today.
 *
 * An unmarked tag line is never deleted: `/pages/free-pet-tag` and the other tag
 * PDPs sell a tag with no Care attached, and those carts are legitimate.
 */
(function () {
  if (window.SnCarePair) return;

  /* The Care subscription half. `supernormal-care` is the paid product Recharge's
     workflow swaps the trial onto — it should never reach a storefront cart, but
     if it ever does it is half a pair just the same. */
  var CARE_HANDLES = ['supernormal-care-trial', 'supernormal-care'];

  /* Tag products sold on the Care template. Only used for carts created before
     `_care_bundle` existed: a marked line is always preferred. */
  var TAG_HANDLES = ['supernormal-qr-lifetime-tracking-pet-tag', 'pawtag-care-trial'];

  var BUNDLE_PROP = '_care_bundle';
  var PETS_PROP = '_care_pets';

  var STRINGS = {
    en: {
      removeBoth: 'Remove both',
      /* engraved tags with different engravings are one line each */
      removeAll: 'Remove all',
      note: 'Your tag and Care trial are bought together.'
    },
    de: {
      removeBoth: 'Beide entfernen',
      removeAll: 'Alle entfernen',
      note: 'Anhänger und Care-Testphase werden zusammen gekauft.'
    }
  };

  function strings() {
    var lang = (document.documentElement.getAttribute('lang') || 'en').toLowerCase();
    return lang.indexOf('de') === 0 ? STRINGS.de : STRINGS.en;
  }

  function route(name, fallback) {
    var routes = (window.theme && theme.routes) || {};
    if (routes[name]) return routes[name];
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return root.replace(/\/$/, '') + fallback;
  }

  var lockedKeys = [];
  var inFlight = null;
  var queued = false;
  var observer = null;

  /* ── Reading the cart ─────────────────────────────────────────────────── */

  function classify(cart) {
    var items = (cart && cart.items) || [];
    var care = [];
    var marked = [];
    var legacyTags = [];

    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var props = item.properties || {};
      if (CARE_HANDLES.indexOf(item.handle) !== -1) care.push(item);
      else if (props[BUNDLE_PROP]) marked.push(item);
      else if (TAG_HANDLES.indexOf(item.handle) !== -1) legacyTags.push(item);
    }

    /* Fall back to handle matching only for a cart that predates the marker, and
       only while a Care line is there to pair with. */
    var tags = marked.length ? marked : (care.length ? legacyTags : []);
    return { care: care, tags: tags, marked: marked };
  }

  /* Returns { updates, locked } — the cart writes needed to restore the
     invariant, and the line keys that make up the pair once it holds. */
  function evaluate(cart) {
    var groups = classify(cart);
    var updates = {};
    var locked = [];

    if (groups.care.length && !groups.tags.length) {
      /* A subscription with nothing to attach it to. */
      groups.care.forEach(function (item) { updates[item.key] = 0; });
    }
    else if (!groups.care.length && groups.marked.length) {
      /* The tag was only free as half of the pair. Unmarked tags are left alone —
         they were never part of one. */
      groups.marked.forEach(function (item) { updates[item.key] = 0; });
    }
    else if (groups.care.length && groups.tags.length) {
      /* The Care rung was priced for the pet count chosen on the product page, and
         the cart cannot re-rung it, so the tag quantity is pinned to that count. */
      if (groups.marked.length === 1) {
        var pets = parseInt(groups.marked[0].properties[PETS_PROP], 10);
        if (pets > 0 && groups.marked[0].quantity !== pets) {
          updates[groups.marked[0].key] = pets;
        }
      }
      /* One subscription covers every pet, so there is exactly one Care line. A second
         one is a leftover (two overlapping add-to-carts left two, 2026-09-30): keep the
         newest - /cart.js lists the most recently added line first - and drop the rest. */
      groups.care.forEach(function (item, i) {
        if (i > 0) updates[item.key] = 0;
        else if (item.quantity !== 1) updates[item.key] = 1;
      });

      groups.care.slice(0, 1).concat(groups.tags).forEach(function (item) { locked.push(item.key); });
    }

    return { updates: updates, locked: locked };
  }

  /* ── Writing ──────────────────────────────────────────────────────────── */

  function bundledSections() {
    var sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sections } })
    );
    return sections;
  }

  function rerender(parsedState, sections) {
    var published = false;
    if (sections.length && window.theme && theme.pubsub && theme.pubsub.PUB_SUB_EVENTS) {
      try {
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: 'sn-care-pair',
          cart: parsedState
        });
        published = true;
      }
      catch (e) { /* fall through to the reload below */ }
    }
    if (published) return;

    /* No section to swap in (or the theme threw): the cart page has to be redrawn
       from the server, everywhere else the drawer can refetch itself. */
    if (/\/cart\/?$/.test(window.location.pathname)) window.location.reload();
    else document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));
  }

  /* One /cart/change.js call per line, keyed by line item key — the same call the
     theme's own cart-items makes, so `id` is known to accept a key here. Line item
     keys are stable, so removing one line does not renumber the others. Only the
     last call asks for the rendered sections: they are what gets drawn. */
  function write(updates) {
    var keys = Object.keys(updates);
    if (!keys.length) return Promise.resolve();

    var sections = bundledSections();
    var chain = Promise.resolve(null);

    keys.forEach(function (key, i) {
      chain = chain.then(function () {
        var body = { id: key, quantity: updates[key] };
        if (i === keys.length - 1) body.sections = sections;
        return fetch(route('cart_change_url', '/cart/change.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        }).then(function (r) { return r.json(); });
      });
    });

    return chain.then(function (parsedState) { rerender(parsedState, sections); });
  }

  /* ── Layer 1: lock the pair in the rendered cart ───────────────────────── */

  function injectStyle() {
    if (document.getElementById('sn-care-pair-style')) return;
    var style = document.createElement('style');
    style.id = 'sn-care-pair-style';
    style.textContent =
      '.sn-care-pair-note{display:block;margin-top:.25rem;opacity:.65;line-height:1.3}' +
      '.sn-care-pair-locked .quantity__buttons{display:none}' +
      '.sn-care-pair-locked .quantity__input{opacity:.55}';
    document.head.appendChild(style);
  }

  function applyLock() {
    if (!lockedKeys.length) return;
    injectStyle();

    var copy = strings();
    var keys = {};
    lockedKeys.forEach(function (key) { keys[key] = true; });

    Array.prototype.forEach.call(document.querySelectorAll('[data-index]'), function (el) {
      if (!keys[el.getAttribute('data-index')]) return;

      if (el.getAttribute('is') === 'cart-remove-button') {
        if (el.getAttribute('data-sn-care-pair') === 'remove') return;
        el.setAttribute('data-sn-care-pair', 'remove');
        el.textContent = lockedKeys.length > 2 ? copy.removeAll : copy.removeBoth;

        /* The note goes in the line's details column, under the price. Next to the
           Remove link it sat in the quantity column, which is shrink-0 and sizes to
           its widest child - the one-line note blew it out to ~250px, crushing the
           title and pushing Remove off the edge of the drawer (2026-09-25). */
        /* drawer: .horizontal-product__details; /cart page: .cart-item__details */
        var line = el.closest('.horizontal-product, .cart-item, tr, li');
        var host = (line && line.querySelector('.horizontal-product__details, .cart-item__details')) || el.parentNode;
        if (host && !(line || host).querySelector('.sn-care-pair-note')) {
          var note = document.createElement('span');
          note.className = 'sn-care-pair-note text-xs';
          note.textContent = copy.note;
          host.appendChild(note);
        }
        return;
      }

      if (el.tagName === 'INPUT' && el.name === 'updates[]') {
        el.disabled = true;
        el.setAttribute('data-sn-care-pair', 'qty');
        var wrapper = el.closest('quantity-input') || el.parentNode;
        if (wrapper && wrapper.classList) wrapper.classList.add('sn-care-pair-locked');
      }
    });
  }

  /* The drawer and the cart page both redraw by replacing innerHTML, which throws
     the lock away. Watch for it rather than trying to hook every redraw path. */
  function watchDom() {
    if (!lockedKeys.length) {
      if (observer) { observer.disconnect(); observer = null; }
      return;
    }
    if (observer || !window.MutationObserver) return;

    var pending = null;
    observer = new MutationObserver(function () {
      if (pending) return;
      pending = window.setTimeout(function () { pending = null; applyLock(); }, 50);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* A locked row's "Remove" clears the whole pair. Bound in the capture phase so
     it runs before cart-remove-button's own listener on the element. */
  function onClickCapture(event) {
    var link = event.target && event.target.closest
      ? event.target.closest('[data-sn-care-pair="remove"]')
      : null;
    if (!link || !lockedKeys.length) return;

    event.preventDefault();
    event.stopPropagation();

    var updates = {};
    lockedKeys.forEach(function (key) { updates[key] = 0; });
    lockedKeys = [];
    watchDom();
    write(updates);
  }

  /* Belt and braces for the quantity fields: `disabled` already stops them, but a
     redraw can land between the swap and the next applyLock(). */
  function onChangeCapture(event) {
    var target = event.target;
    if (!target || target.name !== 'updates[]') return;
    if (!lockedKeys.length) return;
    if (lockedKeys.indexOf(target.getAttribute('data-index')) === -1) return;

    event.preventDefault();
    event.stopPropagation();
    target.value = target.defaultValue;
    sync();
  }

  /* ── Layer 2: the cart itself is the truth ────────────────────────────── */

  function run() {
    return fetch(route('cart_url', '/cart') + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var result = evaluate(cart);
        lockedKeys = result.locked;

        if (Object.keys(result.updates).length) {
          /* Repairing republishes the cart, which re-enters sync(). That second
             pass finds the invariant holding and only applies the lock. */
          return write(result.updates);
        }

        applyLock();
        watchDom();
      })
      .catch(function () { /* never block the purchase flow on this */ });
  }

  /* Serialised, with at most one follow-up queued: the repair's own re-render
     fires cart:updated while this pass is still open, and that pass is the one
     that puts the lock back on the freshly drawn rows. */
  function sync() {
    if (inFlight) {
      queued = true;
      return inFlight;
    }
    inFlight = run().then(function () {
      inFlight = null;
      if (queued) {
        queued = false;
        return sync();
      }
    });
    return inFlight;
  }

  window.SnCarePair = { sync: sync };

  function onReady() {
    document.addEventListener('click', onClickCapture, true);
    document.addEventListener('change', onChangeCapture, true);

    sync();

    document.addEventListener('cart:updated', function () { sync(); });
    document.addEventListener('cart:refresh', function () { sync(); });
    if (window.theme && theme.pubsub && theme.pubsub.subscribe && theme.pubsub.PUB_SUB_EVENTS) {
      theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, function () { sync(); });
    }
    /* Coming back from checkout is a bfcache restore, not a fresh load. */
    window.addEventListener('pageshow', function (event) { if (event.persisted) sync(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }
})();
